import React, { useEffect, useMemo, useState } from 'react';
import api from '../wrapper/API';
import CustomDropdown from '../input/CustomDropdown';
import StatsCard from './StatsCard';
import { readThemeColors } from './chartColors';
import { ChartTooltip } from './ChartTooltip';
import { buildAttendanceSegments, formatAttendanceValue } from './attendanceSegments';
import { addOnIconColor, addOnIncomeBars } from './addOnStats';
import IncomeBreakdownChart from './IncomeBreakdownChart';
import { useDarkMode } from '../context/DarkModeContext';
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid, Label
} from 'recharts';

const MONTH_LABELS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const fmtEuro = (cents) => ((cents ?? 0) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtTooltipEuro = (v) => `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const axisStyle = (colors) => ({
  tick: { fill: colors.text, fontSize: 12, fontFamily: 'var(--font-mono)' },
  axisLine: false,
  tickLine: false,
});

function DonutLabel({ viewBox, total, sublabel, colors }) {
  const { cx, cy } = viewBox;
  return (
    <text textAnchor="middle">
      <tspan x={cx} y={cy - 8} fontSize="22" fontWeight="700" fill={colors.text} fontFamily="var(--font-body)">{total}</tspan>
      <tspan x={cx} y={cy + 14} fontSize="11" fill={colors.textSecondary} fontFamily="var(--font-body)">{sublabel}</tspan>
    </text>
  );
}

// Events per kind for the "Events" card: templateCounts (per process template) merged by event label —
// several templates may share one label.
function eventKindCounts(stats) {
  const counts = new Map();
  const add = (label, count) => counts.set(label, (counts.get(label) ?? 0) + (count ?? 0));
  (stats?.templateCounts ?? []).forEach(t => add(t.eventLabel || t.name, t.count));
  return [...counts]
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'de'))
    .map(([label, count]) => `${label}: ${count}`);
}

function YearView() {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const { isDarkMode } = useDarkMode();
  // readThemeColors() reads CSS variables at runtime; it has no arg but depends on the active theme.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colors = useMemo(() => readThemeColors(), [isDarkMode]);

  useEffect(() => {
    api.get('/Statistics/GetAvailableYears')
      .then(res => {
        setYears(res.data);
        if (res.data.length > 0) setSelectedYear(res.data[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedYear == null) return;
    setLoading(true);
    api.get(`/Statistics/GetYearStats?year=${selectedYear}`)
      .then(res => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [selectedYear]);

  if (years.length === 0) {
    return <p className="statsEmpty">Noch keine Events angelegt.</p>;
  }

  const monthlyData = (stats?.monthly ?? []).map(m => ({
    month: MONTH_LABELS[m.month - 1],
    Einnahmen: m.incomeCents / 100,
    'Lohn Spieltagleiter': m.orgaSalaryCents / 100,
    Pauschalen: (m.flatRateCents ?? 0) / 100,
    'Abzüge': (m.deductionCents ?? 0) / 100,
    Netto: m.netCents / 100,
    Events: m.eventCount + (m.cancelledCount ?? 0),
    'Davon abgesagt': m.cancelledCount ?? 0,
    Teilnahmen: m.participations
  }));

  const attendanceData = stats ? buildAttendanceSegments(stats.roles, stats.participantGroups, colors) : [];
  const eventKinds = stats ? eventKindCounts(stats) : [];
  // Flat rates of the year, one line per flat rate (summed over all events, largest first).
  const flatRateLines = (stats?.flatRates ?? []).map(f => `${f.name}: ${fmtEuro(f.totalCents)} €`);
  // Deductions ("Abzüge") of the year, listed the same way.
  const deductionLines = (stats?.deductions ?? []).map(d => `${d.name}: ${fmtEuro(d.totalCents)} €`);
  // Own bar color for "Abzüge", distinct from positive/negative/warning/accent (theme-aware palette token).
  const deductionColor = colors.participantGroup?.purple || colors.textSecondary;
  // Subtotal before the flat rates: income minus leader salary.
  const afterSalaryCents = (stats?.totalIncomeCents ?? 0) - (stats?.totalOrgaSalaryCents ?? 0);
  // Add-ons of the year by (resolved) name: income bars ("Eintritt" + one per add-on) and usage cards.
  const yearAddOns = Array.isArray(stats?.addOns) ? stats.addOns : [];
  const incomeSplit = stats ? [
    { name: 'Eintritt', Betrag: (stats.totalEntryCents ?? 0) / 100 },
    ...addOnIncomeBars(yearAddOns),
  ].filter(d => d.Betrag > 0) : [];
  const addOnUsage = yearAddOns.filter(a => (a.used ?? 0) > 0);

  const topAttendees = (stats?.topAttendees ?? []).map(a => ({
    name: `${a.firstName} ${a.lastName}`,
    Anwesenheiten: a.attendances
  }));

  const attendanceTotal = attendanceData.reduce((s, d) => s + (d.value || 0), 0);

  return (
    <div className="statsYearView">
      <div className="statsFilterRow">
        <label className="statsFilterLabel">Jahr:</label>
        <CustomDropdown
          icon="calendar_month"
          value={String(selectedYear ?? '')}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </CustomDropdown>
      </div>

      {loading && <p className="statsEmpty">Lade Jahresstatistiken...</p>}

      {stats && !loading && (
        <>
          <div className="statsCardGrid">
            <StatsCard title="Einnahmen"        value={fmtEuro(stats.totalIncomeCents)}     suffix="€" icon="payments"     tone="positive" />
            <StatsCard title="Lohn Spieltagleiter" value={fmtEuro(stats.totalOrgaSalaryCents)} suffix="€" icon="sports"     tone="negative" sub={`${stats.totalOrgaShifts} Spieltagleiter-Einsätze`} />
            <StatsCard title="Einnahmen abzüglich Lohn" value={fmtEuro(afterSalaryCents)} suffix="€" icon="account_balance_wallet" tone={afterSalaryCents >= 0 ? 'positive' : 'negative'} sub="Einnahmen − Lohn Spieltagleiter" />
            <StatsCard title="Pauschalen"       value={fmtEuro(stats.totalFlatRateCents)}   suffix="€" icon="request_quote" tone="negative" subLines={flatRateLines} sub="Keine Pauschalen" />
            <StatsCard title="Abzüge"           value={fmtEuro(stats.totalDeductionCents ?? 0)} suffix="€" icon="remove_circle" tone="negative" subLines={deductionLines} sub="Keine Abzüge" />
            <StatsCard title="Netto (Gewinn)"   value={fmtEuro(stats.netCents)}             suffix="€" icon="savings"      tone={stats.netCents >= 0 ? 'positive' : 'negative'} />
            <StatsCard title="Events"           value={stats.eventCount + stats.cancelledCount}        icon="event"        tone="accent"   subLines={[eventKinds.join(' · '), stats.cancelledCount > 0 ? `${stats.cancelledCount} davon abgesagt` : null]} />
            <StatsCard title="Teilnahmen"       value={stats.totalParticipations}                      icon="group"        tone="neutral"  sub={`Ø ${stats.averageParticipantsPerEvent} pro Event`} />
            <StatsCard title="Unique Spieler"   value={stats.uniqueParticipants}                       icon="person_pin"   tone="neutral" />
            <StatsCard title="No-Shows"         value={stats.noShowCount}                              icon="person_off"   tone={stats.noShowCount > 0 ? 'warning' : 'neutral'} />
            {/* One "Nutzungen" card per add-on of the year (by name, only add-ons that were booked); the icon takes
                the add-on color (none = default). */}
            {addOnUsage.map(a => (
              <StatsCard
                key={`addon-${a.name}`}
                title={a.name}
                value={a.used}
                icon="inventory_2"
                iconColor={addOnIconColor(a.colorKey)}
                tone="neutral"
                sub={(a.cents ?? 0) > 0 ? `Nutzungen · ${fmtEuro(a.cents)} €` : 'Nutzungen'}
              />
            ))}
          </div>

          <div className="statsChartGrid">
            <div className="statsChartCard wide">
              <h3>Finanzen pro Monat</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthlyData} barCategoryGap="30%">
                  <defs>
                    <linearGradient id="yvGradPos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors.positive} stopOpacity={0.9} />
                      <stop offset="100%" stopColor={colors.positive} stopOpacity={0.5} />
                    </linearGradient>
                    <linearGradient id="yvGradNeg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors.negative} stopOpacity={0.9} />
                      <stop offset="100%" stopColor={colors.negative} stopOpacity={0.5} />
                    </linearGradient>
                    <linearGradient id="yvGradWarn" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors.warning} stopOpacity={0.9} />
                      <stop offset="100%" stopColor={colors.warning} stopOpacity={0.5} />
                    </linearGradient>
                    <linearGradient id="yvGradDed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={deductionColor} stopOpacity={0.9} />
                      <stop offset="100%" stopColor={deductionColor} stopOpacity={0.5} />
                    </linearGradient>
                    <linearGradient id="yvGradAcc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors.accent} stopOpacity={0.9} />
                      <stop offset="100%" stopColor={colors.accent} stopOpacity={0.5} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={colors.borderMuted} strokeDasharray="4 4" vertical={false} />
                  <XAxis dataKey="month" {...axisStyle(colors)} />
                  <YAxis {...axisStyle(colors)} tickFormatter={(v) => `${v}€`} width={64} />
                  <Tooltip
                    isAnimationActive={false}
                    content={({ active, payload, label }) => (
                      <ChartTooltip active={active} payload={payload} label={label} formatValue={fmtTooltipEuro} />
                    )}
                    cursor={{ fill: colors.borderMuted, opacity: 0.4 }}
                  />
                  <Legend wrapperStyle={{ color: colors.text, fontSize: 13, paddingTop: 8 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="Einnahmen"            fill="url(#yvGradPos)"  radius={[3, 3, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="Lohn Spieltagleiter"  fill="url(#yvGradNeg)"  radius={[3, 3, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="Pauschalen"           fill="url(#yvGradWarn)" radius={[3, 3, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="Abzüge"               fill="url(#yvGradDed)"  radius={[3, 3, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="Netto"                fill="url(#yvGradAcc)"  radius={[3, 3, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="statsChartCard wide">
              <h3>Einnahmen-Breakdown</h3>
              {incomeSplit.length === 0 ? (
                <p className="statsEmpty">Keine Einnahmen.</p>
              ) : (
                <IncomeBreakdownChart data={incomeSplit} colors={colors} gradientId="yvGradBreak" />
              )}
            </div>

            <div className="statsChartCard">
              <h3>Events pro Monat</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={monthlyData}>
                  <CartesianGrid stroke={colors.borderMuted} strokeDasharray="4 4" vertical={false} />
                  <XAxis dataKey="month" {...axisStyle(colors)} />
                  <YAxis {...axisStyle(colors)} allowDecimals={false} />
                  <Tooltip
                    isAnimationActive={false}
                    content={({ active, payload, label }) => (
                      <ChartTooltip active={active} payload={payload} label={label} />
                    )}
                  />
                  <Legend wrapperStyle={{ color: colors.text, fontSize: 13, paddingTop: 8 }} iconType="circle" iconSize={8} />
                  <Line type="monotone" dataKey="Events"     stroke={colors.accent}   strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 0, fill: colors.accent }} />
                  <Line type="monotone" dataKey="Davon abgesagt" stroke={colors.negative} strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 0, fill: colors.negative }} strokeDasharray="4 3" />
                  <Line type="monotone" dataKey="Teilnahmen" stroke={colors.positive} strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 0, fill: colors.positive }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="statsChartCard">
              <h3>Gruppen &amp; Rollen</h3>
              {attendanceData.length === 0 ? (
                <p className="statsEmpty">Keine Teilnahmen erfasst.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={attendanceData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={52}
                      outerRadius={82}
                      paddingAngle={2}
                      strokeWidth={0}
                    >
                      {attendanceData.map((entry) => <Cell key={entry.key} fill={entry.color} />)}
                      <Label
                        content={({ viewBox }) => (
                          <DonutLabel viewBox={viewBox} total={attendanceTotal} sublabel="Gesamt" colors={colors} />
                        )}
                      />
                    </Pie>
                    <Tooltip
                      isAnimationActive={false}
                      content={({ active, payload }) => (
                        <ChartTooltip active={active} payload={payload} formatValue={formatAttendanceValue} />
                      )}
                    />
                    <Legend wrapperStyle={{ color: colors.text, fontSize: 13 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="statsChartCard wide">
              <h3>Top 10 Anwesenheiten (Gastspieler)</h3>
              {topAttendees.length === 0 ? (
                <p className="statsEmpty">Keine Teilnahmen erfasst.</p>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, topAttendees.length * 34)}>
                  <BarChart data={topAttendees} layout="vertical" margin={{ left: 40 }}>
                    <defs>
                      <linearGradient id="yvGradTop" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={colors.accent} stopOpacity={0.9} />
                        <stop offset="100%" stopColor={colors.accent} stopOpacity={0.5} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={colors.borderMuted} strokeDasharray="4 4" horizontal={false} />
                    <XAxis type="number" {...axisStyle(colors)} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" {...axisStyle(colors)} width={120} />
                    <Tooltip
                      isAnimationActive={false}
                      content={({ active, payload, label }) => (
                        <ChartTooltip active={active} payload={payload} label={label} />
                      )}
                    />
                    <Bar dataKey="Anwesenheiten" fill="url(#yvGradTop)" radius={[0, 3, 3, 0]} maxBarSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default YearView;
